import React, { useEffect } from "react";
import { useState, useCallback } from "react";
import {
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import ActiveProcessingPage from "./ezev-fx-components/ActiveProcessingPage";
import LandingPage from "./ezev-fx-components/LandingPage";
import TableController from "./ezev-fx-components/TableController";
import SingleVclController from "./ezev-fx-components/SingleVclController";
import InputsView from "./ezev-fx-components/InputsView";
import "../component-css/ezev-fx-stylesheets/ezev-fx-tables-v6.css";
import "../component-css/ezev-fx-stylesheets/ezev-fx-master-stylesheet.css";
import {formatEmail, formatEnergyGraphData, getFuelType, vehicleIdDisplay} from "./ezev-fx-components/HelperFunctions";
import { processApiResponse } from "./ezev-fx-components/utils/ConformUnits";
import { calcEffectiveBattery } from "./ezev-fx-components/HelperFunctions";
import { convertISOToExcelSerial } from "./ezev-fx-components/FormatExcelData";

const USABLE_BATTERY_PCT = 0.9;
const MAX_FAILURES_TO_UPDATE_PROCESSING_ALLOWED = 5;
const UPDATE_MILLISECONDS = 1000;

export default function EzevFx(props) {
    const { apiURL, dbName, dbDisplayName, dbSettings, user, history, products } = props;

    const [activity, setActivity] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [groups, setGroups] = useState(null);
    const [authorizedGroups, setAuthorizedGroups] = useState(null);
    const [populatedGroups, setPopulatedGroups] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupMinDate, setGroupMinDate] = useState(null);
    const [groupMaxDate, setGroupMaxDate] = useState(null);
    const [hasReceivedDates, setHasReceivedDates] = useState(null);
    const [isAnalyticsProcessing, setIsAnalyticsProcessing] = useState(null);
    const [isAggregateProcessing, setIsAggregateProcessing] = useState(null);
    const [processingCountdownFinished, setProcessingCountdownFinished] = useState(null);
    const [isFxOdometer, setIsFxOdometer] = useState(null);
    const [fleetEzevMetrics, setFleetEzevMetrics] = useState(null);
    const [groupEzevMetrics, setGroupEzevMetrics] = useState(null);
    const [totalGroupEzevMetrics, setTotalGroupEzevMetrics] = useState(null);
    const [groupRecommendedCount, setGroupRecommendedCount] = useState(null);
    const [groupEnergyGraphData, setGroupEnergyGraphData] = useState(null);
    const [selectedVehicleCategory, setSelectedVehicleCategory] = useState("All Vehicles");
    const [fuelingData, setFuelingData] = useState(null);
    const [vehicleTransactionCounts, setVehicleTransactionCounts] = useState({});
    const [apiError, setApiError] = useState(null);
    const [vehicleCount, setVehicleCount] = useState(null);
    const [dbCobrandingLogoUrl, setDbCobrandingLogoUrl] = useState(null);
    const [vehicleClasses, setVehicleClasses] = useState(null);

    // Fetch fxns
    const getGroupAccess = useCallback(() => {
        if (!user) return;
        let userEmail = formatEmail(user.email)
        fetch(
          `${apiURL}getGroupAccess?ident=${dbName}&username=${userEmail}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        )
          .then((data) => {
            return data.json();
          })
          .then((data) => {
            let groups = [];
            if (data.groups) groups = data.groups;
            if (groups.length === 0) {
                setPopulatedGroups(["No Groups"]);
            }
            else {
              groups.forEach((g) => {
                groups.push(g.group_id);
              });
              return (groups);
            }
          })
          .then((groups) => {
            fetch(
              `${apiURL}getGroupChildren?ident=${dbName}`,
              { headers: { Authorization: `Bearer ${user.token}` } }
            )
              .then((res) => {
                return res.json();
              })
              .then((r) => {
    
                if (groups) {
                  r.data.forEach((g) => {
                    if (groups.indexOf(g.id) === -1) groups.push(g.id);
                  });
                }
                setAuthorizedGroups(groups);
              });
          });
    }, [apiURL, user, dbName])

    const getVehicleClasses = useCallback(() => {
        fetch(`${apiURL}getVehicleClasses?ident=${dbName}`, { headers: { 'Authorization': `Bearer ${user.token}` } })
        .then((res) => res.json())
        .then((data) => {
            data.data.forEach((vc) => {
              vc = processApiResponse(user.userSettings, vc);
            });
            setVehicleClasses(data.data);
        });
    }, [apiURL, dbName, user]);

    const getCandidates = useCallback(() => {
        var url = `${apiURL}getCandidates`;
        var queries = `?ident=${dbName}`;
        return fetch(`${url}${queries}`, { headers: { 'Authorization': `Bearer ${user.token}` } })
          .then(res => res.json())
          .then(data => {
            data['data'].forEach((u) => {
              u.upfits = u.upfits === null ? [] : u.upfits
              if(u.usable_kwh === null){
                if(u.battery_kwh !== null && u.battery_kwh !== undefined){
                  // note: this logic is also present in analytics. If it is changed in one place, it needs to be changed in the other.
                  // replace with 90% of battery capacity (incl where zero)
                  u.usable_kwh = u.battery_kwh * USABLE_BATTERY_PCT;
                }
              }
              // conform units
              u = processApiResponse(user.userSettings, u);
            })
            setCandidates(data['data']);
          })
    }, [apiURL, dbName, user])

    const getGroups = useCallback((ld = false, all = true) => {
        if (!authorizedGroups) return;
        const sortByName = function (a, b) {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        };
        fetch(`${apiURL}getGroups/${dbName}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
          .then((res) => {
            return res.json();
          })
          .then((r) => {
            let arr = r.data;
            arr.sort(sortByName);
            const idx = arr.findIndex((o) => { return o.id === 'swt-vehicles' });
            if (idx > -1) {
              const g = arr[idx];
              arr.splice(idx, 1)
              arr.unshift(g);
            }
            var newSelectedGroup = {}
            if (arr.length === 0) {
              arr = ["No Groups"]
              newSelectedGroup = { selectedGroup: "No Groups" }
            }
            else {
                newSelectedGroup = arr[idx]; // Assign selected group to default all vehicles on load
            }
            //Update the selected group to no groups if no groups were returned
            //Doing this avoids all of the refetching associated with a full handleGroupChange
            setGroups(arr);
            setPopulatedGroups(arr);
            setSelectedGroup(newSelectedGroup);
          });
    }, [dbName, apiURL, authorizedGroups, user])

    const getActivity = useCallback(() => {
        //Abort fetch if the category isn't selected yet
        if (!selectedVehicleCategory) {
          return;
        }
        const {ld, all_categories} = getVehicleCategoryFlags(selectedVehicleCategory);
        let group = selectedGroup;
        let minCelsius = undefined;
        let maxCelsius = undefined;

        // fetch data for all vcls
        let url = `${apiURL}getVehicleResults`;
        let queries = `?dbName=${dbName}`;
    
        queries += `&groupId=${group.id}`;
        queries += `&is_ld=${ld}&all_categories=${all_categories}&is_td=false`;
    
        fetch(`${url}${queries}`, { headers: { 'Authorization': `Bearer ${user.token}` } })
          .then(res => res.json())
          .then(data => {
            url = `${apiURL}getVehicleUpfits`;
            queries = `?dbName=${dbName}`;
            fetch(`${url}${queries}`, { headers: { 'Authorization': `Bearer ${user.token}` }})
              .then(res => res.json())
              .then(upfits => {
                data['data'].forEach((v) => {
                  // get upfits
                  v.upfits = upfits['data'].filter(u => u.vin === v.vin);
                  v.has_upfits = v.upfits.length > 0 ? 'True' : 'False';
                  v.required_upfits = v.upfits.filter(e => e.required);
                  v.optional_upfits = v.upfits.filter(e => !e.required);

                  //calc activity/group min/max celsius vals
                  if(!minCelsius)minCelsius = v.min_celsius;
                  if(!maxCelsius)maxCelsius = v.max_celsius;
                  minCelsius = Math.min(v.min_celsius, minCelsius);
                  maxCelsius = Math.max(v.max_celsius, maxCelsius); 

                  let cand = candidates.find(c => c.id === v.scored_on_id);
                  let iceCand = candidates.find(c => c.id === v.ice_comp_id);

                  // get vehicle effective usable kWh for PDFs
                  // calc effective battery capacity for primary candidate
                  v.rec_effective_battery_kwh = calcEffectiveBattery(v, dbSettings, cand?.usable_kwh);

                  v.scored_on_maint = cand?.forecasted_maint_per_km;
                  v.ice_comp_maint = iceCand?.forecasted_maint_per_km;
                  
                  // Convert min/max timestamps from ISO to Excel Serial, to ensure dates are still properly sortable in spreadsheet download.
                  // These variables need to be assigned before the vehicle's data is ran through processAPIResponse since all values containing '_ts' are are then converted to strings matching the user's date format, instead of the original timestamp
                  let excel_min_ts = convertISOToExcelSerial(v.min_ts);
                  let excel_max_ts = convertISOToExcelSerial(v.max_ts);
    
                  // Conform units
                  v = processApiResponse(user.userSettings, v);
    
                  // Once the API response is processed, add the serial-formatted timestamps back to the vehicle object for use in excel downloads
                  v.excel_sortable_min_ts = excel_min_ts;
                  v.excel_sortable_max_ts = excel_max_ts;
                })
                setActivity(data['data'])
                setVehicleCount(data['data'].length)

                const obj = processApiResponse(user.userSettings, {max_celsius: maxCelsius, min_celsius: minCelsius});
                setFleetEzevMetrics(obj)
               
              });
          })
          .catch((err) => {
            console.error(err);
            setApiError(true);
          });
    }, [apiURL, dbSettings, user, candidates, selectedGroup, dbName, selectedVehicleCategory])

    const getGroupEzevMetrics = useCallback(() => {
        const url = `${apiURL}getGroupEzevMetrics?ident=${dbName}&group=${selectedGroup.id}`;
        fetch(url, {headers: { Authorization: `Bearer ${user.token}` }})
          .then(res => res.json())
          .then((data) => {
            data['data'].forEach((d) => {
              d = processApiResponse(user.userSettings, d);
            })
            const obj = data.data;
            setGroupEzevMetrics(obj);
          })
          .catch((err) => {
            console.error(err);
            setApiError(true);
          });
    }, [dbName, selectedGroup, user, apiURL]);
    
    const getTotalGroupEzevMetrics = useCallback(() => {
        const url = `${apiURL}getTotalGroupMetrics?ident=${dbName}&group=${selectedGroup.id}`;
        fetch(url, {headers: { Authorization: `Bearer ${user.token}` }})
          .then(res => res.json())
          .then((data) => {
            const obj = processApiResponse(user.userSettings, data.data[0])
            setTotalGroupEzevMetrics(obj);
          })
          .catch((err) => {
            console.error(err);
            setApiError(true);
          });
    }, [dbName, selectedGroup, user, apiURL]);
    
    const getGroupMetrics = useCallback(() => {
        const url = `${apiURL}getGroupMetrics?ident=${dbName}&group=${selectedGroup.id}`;
        fetch(url, {headers: { Authorization: `Bearer ${user.token}` }})
          .then(res => res.json())
          .then((data) => {
            const obj = processApiResponse(user.userSettings, data.data);
            setGroupMinDate(obj.min_ts);
            setGroupMaxDate(obj.max_ts);
            setHasReceivedDates(true);
          })
          .catch((err) => {
            console.error(err);
            setApiError(true);
          });
    }, [apiURL, selectedGroup, dbName, user]);

    const getGroupRecommendedCount = useCallback(() => {
        if (!selectedVehicleCategory) return;
        const {ld, all_categories} = getVehicleCategoryFlags(selectedVehicleCategory);
        const url = `${apiURL}getGroupRecommendedCount?ident=${dbName}&group=${selectedGroup.id}&is_ld=${ld}&all_categories=${all_categories}`;
        fetch(url, {headers: { Authorization: `Bearer ${user.token}` }})
          .then(res => res.json())
          .then((data) => {
            const obj = data.data[0];
            setGroupRecommendedCount(obj.ev_recs);
          })
          .catch((err) => {
            console.error(err);
            setApiError(true);
          });
    }, [apiURL, selectedGroup, selectedVehicleCategory, dbName, user]);

    
    const getFuelingData = useCallback(() => { 
      // fetch fueling data from the EP & turn it into a map here.
      const fuelingData = {};
      fetch(`${apiURL}getFuelingLocations?dbName=${dbName}`,
        {headers: { Authorization: `Bearer ${user.token}` }
      })
      .then((resp)=>resp.json())
      .then((data) => {
        data.data.forEach((loc)=>{
          // grab the array for the specific vin, or initialize one if it doesn't exist
          if(!fuelingData[loc.vin]){
            fuelingData[loc.vin] = [];
          }
          const arr = fuelingData[loc.vin];
          // and add this location/vin combo to the vehicle's array
          arr.push(loc); 
        });
        const transactionCounts = {};
        Object.entries(fuelingData).forEach(([vin,locList]) => {
          const vehicleTransactionCount = locList.reduce((total, loc) => { return total + parseInt(loc.transaction_count)}, 0); // sum up the counts for each location/vin match to get the total transaction count for the vehicle
          transactionCounts[vin] = vehicleTransactionCount; // add it to the transaction counts array
          locList.forEach(loc => {
            // add the frequency to each location object in the vehicle's array
            loc.frequency = loc.transaction_count / vehicleTransactionCount;
            // determine the title of the location to be displayed based on presence of name and address
            if (loc.name && loc.address) loc.title = `${loc.name}: ${loc.address}`;
            else if (loc.name) loc.title = loc.name;
            else if (loc.address) loc.titlte = loc.address;
            else loc.title = "Name and Address Not Found";
          }) 
        });
        setVehicleTransactionCounts(transactionCounts);
        setFuelingData(fuelingData);
      });
    }, [apiURL, dbName, user])

    const getEnergyGraphData = useCallback(() => {
      const url = apiURL;
      const groupId = selectedGroup.id
      const graphDataUrl = `${url}getEnergyGraphData?clientId=${dbName}&group=${groupId}`;
  
      fetch(graphDataUrl, {
        headers: { Authorization: `Bearer ${user.token}` },
      })
        .then(res => res.json())
        .then(data => {
          const formattedData = formatEnergyGraphData(data,user.userSettings)
          setGroupEnergyGraphData(formattedData);
        })
        .catch(error => console.error("Error fetching vehicle daily activity: ", error));

    }, [apiURL, dbName, user, selectedGroup]);

    const getDbCobrandingLogoUrl = useCallback(() => {
      const vehiclesResultsUrl = `${apiURL}getDbCobrandingLogo?dbName=${dbName}`;
      fetch(vehiclesResultsUrl, { 
        headers: { Authorization: `Bearer ${user.token}` },
        }).then(res => res.json())
        .then(data => {
          const url = data.data;
          setDbCobrandingLogoUrl(url);
        })
        .catch((err) => {
          console.error(err);
        });
    }, [apiURL,dbName,user]);

    const getIsFxOdometer = useCallback(() => {
      const url = `${apiURL}getIsFxOdometer?dbName=${dbName}`;
      fetch(url, {headers: { Authorization: `Bearer ${user.token}` }})
        .then(res => res.json())
        .then((data) => {
          const isOdometer = data.data.is_fx_odometer;
          setIsFxOdometer(isOdometer);
        })
        .catch((err) => {
          console.error(err);
        });
    }, [apiURL,dbName,user.token]);

    // End of fetch fxns

    // Processing check fxns
    const handleProcessingFinished = useCallback(() => {
        // This fxn is called upon an analytics run completion
        if(!selectedGroup) return; // These fetches are group dependent, abort fetch if selectedGroup is not yet set
        
        // Invalidate state that controls loading state of info card
        setActivity(null);
        setFleetEzevMetrics(null);
        setGroupRecommendedCount(null);
        setGroupEnergyGraphData(null);

        // Re-fetch analytics dependent data
        getActivity();
        getGroupMetrics();
        getTotalGroupEzevMetrics();
        getGroupEzevMetrics();
        getGroupRecommendedCount();
        getEnergyGraphData();
      }, [getEnergyGraphData, getActivity, getGroupMetrics, getGroupEzevMetrics, getTotalGroupEzevMetrics, getGroupRecommendedCount, selectedGroup]);

    // Get the state of aggregate processing and set applicable flag
    const getAggregateProcessingState = useCallback(() => {
        const url = `${apiURL}isAggregateProcessing?dbName=${dbName}`;
        return fetch(url, {headers: { Authorization: `Bearer ${user.token}` }})
          .then(res => res.json())
          .then((data) => {
            let processing = data.data[0].aggregate_processing;
            setIsAggregateProcessing(processing);
            return Promise.resolve(processing);
          })
          .catch((err) => {
            console.error(err);
            setApiError(true);
            return Promise.reject(err);
          });
    }, [apiURL, dbName, user]);
    
    // Get the state of an analytics run, set applicable flags, and handle redirect/refetch
    const getAnalyticsProcessingState = useCallback((shouldResetState) => {
        const url = `${apiURL}isAnalyticsProcessing?dbName=${dbName}`;
        return fetch(url, {headers: { Authorization: `Bearer ${user.token}` }})
          .then(res => res.json())
          .then((data) => {
            let processing = data.data[0].analytics_processing;
            setIsAnalyticsProcessing(processing);
            if (processing) {
              // return to the homepage
              history.push("/ezev-fx/landing");
            } else {
              // if analytics is not processing, reset the state if we are initializing or the user updated candidates
              // if analytics is not processing, we don't want to reset state if this function was called from the single vehicle controller
              if (shouldResetState) {
                handleProcessingFinished();
              }
            }
            return Promise.resolve(processing);
          })
          .catch((err) => {
            console.error(err);
            setApiError(true);
            return Promise.reject(err);
          });
    }, [apiURL, dbName, user, history, handleProcessingFinished]);

    // Handles analytics run completion and error states if applicable
    const handleProcessingCountdownFinished = useCallback(() => {
        let failures_to_update = 0;
        const intervalId = setInterval(async () => {
          const isProcessing = await getAnalyticsProcessingState(true);
          
          if (!isProcessing) {
            setProcessingCountdownFinished(null);
            clearInterval(intervalId);
          } else {
            failures_to_update+=1;
          }
          
          if (failures_to_update >= MAX_FAILURES_TO_UPDATE_PROCESSING_ALLOWED) {
            clearInterval(intervalId);
            if (isProcessing) {
                setApiError(true);
            }
          }
        }, UPDATE_MILLISECONDS);
    }, [getAnalyticsProcessingState])

    // End of processing check fxns

    // Start of state control fxns

    // Checks aggregate/analytics processing states on load
    useEffect(() => {
      // Note these are self handling functions, no need to consume the response
      // Promise chained together to preserve our order of operations 
      getAggregateProcessingState()
      .then((res) => {
          getAnalyticsProcessingState(true)
          .then((res) => {
          })
      })
    // eslint-disable-next-line
    }, [])

    // Handles calling analytics processing completion logic if processing is complete
    useEffect(() => {
        if(processingCountdownFinished) handleProcessingCountdownFinished();
    }, [handleProcessingCountdownFinished, processingCountdownFinished])
    
    // Fetch calls that are not dependent on groups
    useEffect(() => {
        getGroupAccess();
        getCandidates();
        getFuelingData();
        getDbCobrandingLogoUrl();
        getIsFxOdometer();
        getVehicleClasses();
    }, [getCandidates, getFuelingData, getGroupAccess, getDbCobrandingLogoUrl, getIsFxOdometer, getVehicleClasses])

    // Get groups separate of other fetches, to avoid dependency loop
    useEffect(() => {
        getGroups();
    }, [getGroups])

    // Group dependent fetch fxns
    useEffect(() => {
        if(!selectedGroup) return; // These fetches are group dependent, abort fetch if selectedGroup is not yet set

        // Set some state to null if we are currently fetching/refetching
        // Info card landing loading bar checks against this state
        setActivity(null);
        setFleetEzevMetrics(null);
        setGroupRecommendedCount(null);
        setGroupEnergyGraphData(null);
        getActivity();
        getGroupEzevMetrics();
        getTotalGroupEzevMetrics();
        getGroupMetrics();
        getGroupRecommendedCount();
        getEnergyGraphData();
    }, [getEnergyGraphData, getActivity, getGroupEzevMetrics, getTotalGroupEzevMetrics, getGroupMetrics, getGroupRecommendedCount, selectedGroup])

    // End of state control functions

    // Start of helper fxns
    const getVehicleCategoryFlags = (vehicleCategory) => {
        let ld = false;
        let all_categories = false;
        if (vehicleCategory === 'Light Duty') ld = true;
        if (vehicleCategory === 'Medium and Heavy Duty') ld = false;
        if (vehicleCategory === 'All Vehicles') {
          all_categories = true;
        }
        return {"ld": ld, "all_categories": all_categories};
    }

    // Handle group change on control change
    const handleGroupSelect = (e, groupId) => {
        setHasReceivedDates(false);
        let group = groups.filter((g) => {
          return g.id === groupId;
        });
        setSelectedGroup(group[0])
    };
    
    // Handle category change on control change
    const handleVehicleCategorySelect = (e) => {
        setSelectedGroup(groups.find(g => g.id === 'swt-vehicles')); // Reset selected group to all vehicles on category change
        setSelectedVehicleCategory(e.value);
    }

    function getYearsRemaining(vcl) {
      let lifecycle = vcl.life_cycle ?? dbSettings.life_cycle;
      let currentYear = new Date().getFullYear();
      let vclYears = currentYear - vcl.year;
      let yearsRemaining = lifecycle - vclYears;
      return yearsRemaining.toString();
    }
    
    const _filteredActivity = () => {
        if(!activity)return null;
        return activity.map(item => {
          item.asset_id = vehicleIdDisplay(item)
          //Add fuel type and coerce nulls to true for is_ld as per global default
          item.fuel_type = getFuelType(item);
          item.years_remaining = getYearsRemaining(item);
          return item;
        })
    }

    // End of helper fxns

    // Render
    if (isAnalyticsProcessing || isAggregateProcessing || apiError) {
        return (
          // Render our processing page if any of the above conditions
          <ActiveProcessingPage
            dbName={dbName}
            user={user}
            apiURL={apiURL}
            dbDisplayName={dbDisplayName}
            setProcessingCountdownFinished={(value) => setProcessingCountdownFinished(value)}
            apiError={apiError}
            aggregateProcessing={isAggregateProcessing}
          />
        );
    }
    else {  
        // Othwerise render the app
        return (
            <div className="ezev-app">
            <div className="ezev-app-content">
                <Route
                exact
                path="/"
                render={(props) => (
                    <Redirect to={{ pathname: "/ezev-fx/" }} />
                )}
                />
                <Route
                exact
                path="/ezev-fx/"
                render={(props) => (
                    <Redirect to={{ pathname: "/ezev-fx/landing" }} />
                )}
                />
                <Switch>
                <Route
                    path="/ezev-fx/landing"
                    render={(props) => (
                    <LandingPage
                        dbName={dbName}
                        group={selectedGroup}
                        groupMinDate={groupMinDate}
                        groupMaxDate={groupMaxDate}
                        hasReceivedDates={hasReceivedDates}
                        groupRecommendedCount={groupRecommendedCount}
                        totalGroupEzevMetrics={totalGroupEzevMetrics}
                        groupEzevMetrics={groupEzevMetrics}
                        fleetEzevMetrics={fleetEzevMetrics}
                        dbDisplayName={dbDisplayName}
                        user={user}
                        apiURL={apiURL}
                        groups={populatedGroups}
                        handleGroupSelect={handleGroupSelect}
                        vehicleCount={vehicleCount}
                        selectedGroupId={
                        selectedGroup
                            ? selectedGroup.id
                            : null
                        }
                        selectedVehicleCategory={selectedVehicleCategory}
                        handleVehicleCategorySelect={handleVehicleCategorySelect}
                        activity={JSON.parse(JSON.stringify(_filteredActivity()))}
                        settings={dbSettings}
                        authorizedGroups={authorizedGroups}
                        fuelingData={fuelingData}
                        vehicleTransactionCounts={vehicleTransactionCounts}
                        candidates={candidates}
                        energyGraphData={groupEnergyGraphData}
                        cobrandingLogoUrl={dbCobrandingLogoUrl}
                        isFxOdometer={isFxOdometer}
                        vehicleClasses={vehicleClasses}
                    />
                    )}
                />
                <Route
                    path="/ezev-fx/vehicle-results"
                    exact
                    render={(props) => (
                    <TableController
                        activity={JSON.parse(JSON.stringify(_filteredActivity()))}
                        group={selectedGroup}
                        multipleGroups={groups?.length > 1}
                        dbDisplayName={dbDisplayName}
                        userSettings={user.userSettings}
                        isFxOdometer={isFxOdometer}
                    />
                    )}
                />

                <Route
                    path="/ezev-fx/vehicle/:vin"
                    exact
                    render={(props) => (
                    <SingleVclController
                        dbName={dbName}
                        dbDisplayName={dbDisplayName}
                        user={user}
                        apiURL={apiURL}
                        localKwh={dbSettings.local_kwh_cost}
                        settings={dbSettings}
                        activity={JSON.parse(JSON.stringify(_filteredActivity()))}
                        candidates={candidates}
                        fuelingData={fuelingData}
                        vehicleTransactionCounts={vehicleTransactionCounts}
                        getAnalyticsProcessingState={getAnalyticsProcessingState}
                        products={products}
                        energyGraphData={groupEnergyGraphData}
                        cobrandingLogoUrl={dbCobrandingLogoUrl}
                        isFxOdometer={isFxOdometer}
                        vehicleClasses={vehicleClasses}
                        {...props}
                    />
                    )}
                />
                <Route
                    path="/ezev-fx/inputs"
                    exact
                    render={(props) => (
                    <InputsView
                        dbName={dbName}
                        user={user}
                        dbDisplayName={dbDisplayName}
                        apiURL={apiURL}
                        settings={dbSettings}
                        userSettings={user.userSettings}
                        candidates={candidates}
                        isFxOdometer={isFxOdometer}
                    />
                    )}
                />
                {/* Catch-all route for undefined paths to redirect to landing page.
                    Note that this can really only happen when running the local repo - post-auth redirect prevents this in the dashboard
                    Adding this now for if we add support in the future for post-auth return to URL,
                    and to prevent weirdness when developing locally - NK 9/24
                */}
                <Route
                  render={() => 
                    <Redirect to="/ezev-fx/landing" />
                  }
                />
                </Switch>
            </div>
            </div>
        );
    }
}